// Applies inferred scheduling when a count run is marked Complete.
//
// The rule itself lives in utils/inferredSchedule.js and is pure. This module
// is the part that touches the database: find the template the finished run
// belongs to, find the completion before it, ask the rule what the schedule
// should now be, and write it.
//
// Every completion path calls this — the in-count Done flow, "Mark complete"
// on the overview, and the offline queue when it drains. It is best-effort by
// construction: a schedule that could not be inferred must never fail a count
// the user has already finished, so nothing here throws and every failure is a
// warning plus `{ changed: false }`.

import { inferScheduleOnComplete } from '../utils/inferredSchedule';
import { inventoryPeriodConfigToCycle } from '../components/cycleHelpers.mjs';

const NOOP = Object.freeze({ changed: false });

/** Local YYYY-MM-DD for a timestamp — the store's calendar day, not UTC's. */
function localDayOf(value) {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Update the template's schedule to match the run that was just completed.
 *
 * @param {object} supabase       an authenticated Supabase client
 * @param {object|string|number} sessionOrId  the count_sessions row that was
 *        completed, or just its id — the offline queue only carries the id, so
 *        the row is read back when that is all we are given.
 * @param {object} [opts]
 * @param {string} [opts.completedAt]  ISO completion timestamp (defaults to now)
 * @param {object} [opts.storePeriodConfig]  stores.inventory_period_config, when
 *        the caller already has it — saves a read for EOP templates that have
 *        no schedule of their own and inherit the store's.
 * @returns {Promise<{changed: boolean, templateId?: any, cycle?: object, reason?: string, gapDays?: number|null}>}
 */
export async function applyInferredScheduleOnComplete(supabase, sessionOrId, opts = {}) {
  try {
    if (!supabase || sessionOrId === null || sessionOrId === undefined) return NOOP;

    let session = sessionOrId;
    if (typeof session !== 'object') {
      const { data, error } = await supabase
        .from('count_sessions')
        .select('id, count_group_id, is_template, store_id, completed_at')
        .eq('id', sessionOrId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return NOOP;
      session = data;
    }
    if (!session.id) return NOOP;
    // A template row is the schedule; it is not a run of one.
    if (session.is_template) return NOOP;

    const completedOn = localDayOf(opts.completedAt || session.completed_at);
    if (!completedOn) return NOOP;

    const groupId = session.count_group_id || session.id;

    // The template parent for this group. A group that never had one
    // materialized has no schedule to keep, so there is nothing to do.
    const { data: templates, error: tplErr } = await supabase
      .from('count_sessions')
      .select('id, ordering_cycle_config, count_purpose, store_id')
      .eq('is_template', true)
      .eq('count_group_id', groupId)
      .limit(1);
    if (tplErr) throw tplErr;
    const template = templates && templates[0];
    if (!template) return NOOP;

    // The completion before this one, in the same group. Ordered by
    // completed_at so a backdated run cannot pose as the most recent.
    let previousCompletedOn = null;
    const { data: priorRows, error: priorErr } = await supabase
      .from('count_sessions')
      .select('completed_at')
      .eq('count_group_id', groupId)
      .eq('status', 'completed')
      .neq('id', session.id)
      .not('completed_at', 'is', null)
      .lt('completed_at', opts.completedAt || session.completed_at || new Date().toISOString())
      .order('completed_at', { ascending: false })
      .limit(1);
    if (priorErr) {
      // A missing previous completion only costs precision — the weekday rule
      // still applies — so this is a warning, not an abort.
      console.warn('[inferredSchedule] previous-completion lookup failed:', priorErr.message);
    } else if (priorRows && priorRows[0]?.completed_at) {
      previousCompletedOn = localDayOf(priorRows[0].completed_at);
    }

    // The schedule in force right now. An end-of-period template with no cycle
    // of its own displays (and runs on) the store's inventory_period_config, so
    // that inherited cycle is what the completion has to be judged against —
    // otherwise a store on a fiscal calendar would look unscheduled and get
    // overwritten on its first completion.
    let currentCycle = template.ordering_cycle_config || null;
    if (!currentCycle && template.count_purpose === 'period_end') {
      const storeCfg = opts.storePeriodConfig !== undefined
        ? opts.storePeriodConfig
        : await readStorePeriodConfig(supabase, template.store_id ?? session.store_id);
      currentCycle = inventoryPeriodConfigToCycle(storeCfg) || null;
    }

    const inferred = inferScheduleOnComplete({ currentCycle, completedOn, previousCompletedOn });
    if (!inferred) return NOOP;

    const { error: updErr } = await supabase
      .from('count_sessions')
      .update({ ordering_cycle_config: inferred.cycle })
      .eq('id', template.id);
    if (updErr) throw updErr;

    console.log(
      `[inferredSchedule] template ${template.id} -> ${inferred.cycle.preset} (${inferred.reason}`
      + `${inferred.gapDays !== null ? `, ${inferred.gapDays}d gap` : ''})`
    );
    return { changed: true, templateId: template.id, ...inferred };
  } catch (e) {
    console.warn('[inferredSchedule] could not update the template schedule:', e?.message || e);
    return NOOP;
  }
}

async function readStorePeriodConfig(supabase, storeId) {
  if (!storeId) return null;
  const { data, error } = await supabase
    .from('stores')
    .select('inventory_period_config')
    .eq('id', storeId)
    .maybeSingle();
  if (error) {
    console.warn('[inferredSchedule] store period config lookup failed:', error.message);
    return null;
  }
  return data?.inventory_period_config || null;
}

export default applyInferredScheduleOnComplete;
